import { useEditSmsSender } from 'modules/sendingPlatforms/hooks';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useModalsContext, useSendingPlatformsQueryContext } from '../../../../contexts';
import { SMS_SENDER_FORM_FIELD_NAMES, SmsSender } from '../../../../types';
import { getPhoneNumberLabelForEspAccount } from '../../../../utils';
import { BaseClearableConnectedInputField } from '../../Shared/ClearableConnectedInputField';
import { ConnectedInputField } from '../../Shared/ConnectedInputField';
import { MessageTypeField } from './MessageTypeField';
import { SmsSenderModalRHF } from './SmsSenderModalRHF';

const { NAME, PHONE_NUMBER, MESSAGE_TYPE_ID } = SMS_SENDER_FORM_FIELD_NAMES;

type Props = {
  smsSender: SmsSender;
};

export const EditSmsSenderModal = ({ smsSender }: Props) => {
  const [phoneNumberEdited, setPhoneNumberEdited] = useState(false);
  const dispatch = useDispatch();
  const { hideModal } = useModalsContext();
  const { refetchSmsSenders, selectEspAccount } = useSendingPlatformsQueryContext();
  const espAccount = selectEspAccount(smsSender.espAccountId);

  const { editSmsSender } = useEditSmsSender({
    onCompleted: ({ updateSmsSender }) => {
      const senderName = updateSmsSender?.success?.name || 'SMS sender';
      dispatch(
        addPositiveToast(
          <span>
            Success! updated: <b>{senderName}</b>
          </span>,
        ),
      );
      refetchSmsSenders();
    },
    onError: ({ message = '' }) => {
      dispatch(addNegativeToast(`Unable to update SMS sender. ${message}`));
    },
  });

  const onSubmit = (values: FieldValues) => {
    const messageTypeId = values.messageTypeId > 0 ? values.messageTypeId : null;
    const phoneNumber = phoneNumberEdited ? values.phoneNumber : smsSender.phoneNumber;

    editSmsSender({
      variables: {
        id: smsSender.id,
        fields: {
          name: values.name,
          phoneNumber,
          phoneNumberType: values.type,
          messageTypeId,
        },
      },
    });
    hideModal();
  };

  return (
    <SmsSenderModalRHF
      confirmText={`Save message profile`}
      headerText={`Edit ${smsSender.name}`}
      onSubmit={onSubmit}
    >
      <ConnectedInputField key={NAME} name={NAME} labelText={'Name'} />
      <BaseClearableConnectedInputField
        labelText={getPhoneNumberLabelForEspAccount(espAccount)}
        name={PHONE_NUMBER}
        tripWasEdited={() => setPhoneNumberEdited(!phoneNumberEdited)}
        wasEdited={phoneNumberEdited}
      />
      <MessageTypeField name={MESSAGE_TYPE_ID} />
    </SmsSenderModalRHF>
  );
};
